import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { IMaterial } from '@interfaces/IMaterial';
import { UserInfoLoadingForTabs } from '@myStore/users/user.actions';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { MaterialsService } from '@services/materials/materials.service';
import { QueryParamsService } from '@services/query-params/query-params.service';
import { ToastrService } from 'ngx-toastr';
import { catchError, map, switchMap } from 'rxjs/operators';
import { IAppState } from '../app.state';
import {
  AddMaterial,
  AddMaterialFailure,
  AddMaterialSuccess,
  DeleteMaterial,
  DeleteMaterialSuccess,
  EMaterialsActions,
  GetMaterial,
  GetMaterials,
  GetMaterialsSuccess,
  GetMaterialSuccess,
  UpdateMaterial,
  UpdateMaterialFailure,
  UpdateMaterialSuccess,
} from './materials.actions';

@Injectable()
export class MaterialsEffects {
  constructor(
    private _actions$: Actions,
    private _store: Store<IAppState>,
    private _materialsService: MaterialsService,
    private _router: Router,
    private _toastr: ToastrService,
    private _qpService: QueryParamsService
  ) {}

  $addMaterial = createEffect(
    () => {
      return this._actions$.pipe(
        ofType<AddMaterial>(EMaterialsActions.AddMaterial),
        map(action => action),
        switchMap(action => {
          return this._materialsService.createMaterial(action.material).pipe(
            map((material: IMaterial) => {
              this._store.dispatch(new AddMaterialSuccess(material));
              window.open(`materials/${material.id}`, '_blank');
              this._toastr.success('Материал успешно добавлен');
              if (action.options?.changeParams) {
                this._qpService.changeQueryParams(action.options.changeParams);
              }
            }),
            catchError(err => {
              let message = 'Не удалось добавить материал';

              if (err && err.error.content_file) {
                message = err.error.content_file[0];
              }

              this._toastr.error(message);
              this._store.dispatch(new AddMaterialFailure(err));
              return err;
            })
          );
        })
      );
    },
    { dispatch: false }
  );

  $getMaterial = createEffect(
    () => {
      return this._actions$.pipe(
        ofType<GetMaterial>(EMaterialsActions.GetMaterial),
        map(action => action),
        switchMap(action => {
          return this._materialsService.getMaterial(action.id).pipe(
            map((material: IMaterial) => {
              this._store.dispatch(new GetMaterialSuccess(material));
            })
          );
        })
      );
    },
    { dispatch: false }
  );

  $getMaterials = createEffect(
    () => {
      return this._actions$.pipe(
        ofType<GetMaterials>(EMaterialsActions.GetMaterials),
        map(action => action),
        switchMap(action => {
          return this._materialsService
            .getMaterials(action.group_id, action.options?.qparams)
            .pipe(
              map((materials: { count: number; results: IMaterial[] }) => {
                this._store.dispatch(new GetMaterialsSuccess(materials));
                this._store.dispatch(new UserInfoLoadingForTabs(false));
              }),
              catchError(err => {
                this._toastr.error(
                  `${
                    err?.error?.detail
                      ? err?.error?.detail
                      : 'Не удалось получить материалы'
                  }`,
                  'Что-то пошло не так',
                  { timeOut: 3500 }
                );
                this._store.dispatch(new UserInfoLoadingForTabs(false));
                return err;
              })
            );
        })
      );
    },
    { dispatch: false }
  );

  $updateMaterial = createEffect(
    () => {
      return this._actions$.pipe(
        ofType<UpdateMaterial>(EMaterialsActions.UpdateMaterial),
        map(action => action),
        switchMap(action => {
          return this._materialsService.updateMaterial(action.material).pipe(
            map(() => {
              this._store.dispatch(new UpdateMaterialSuccess());
            }),
            catchError(err => {
              this._toastr.error(
                `${
                  err?.error?.detail
                    ? err?.error?.detail
                    : 'При обновлении материалов возникла ошибка'
                }`,
                'Что-то пошло не так',
                { timeOut: 3500 }
              );
              this._store.dispatch(new UpdateMaterialFailure());
              return err.statusText;
            })
          );
        })
      );
    },
    { dispatch: false }
  );

  $deleteMaterial = createEffect(
    () => {
      return this._actions$.pipe(
        ofType<DeleteMaterial>(EMaterialsActions.DeleteMaterial),
        map(action => action),
        switchMap(action => {
          return this._materialsService.deleteMaterial(action.material_id).pipe(
            map(() => {
              this._store.dispatch(
                new DeleteMaterialSuccess(action.material_id)
              );
              this._toastr.success('Материал успешно удален');
            }),
            catchError(err => {
              this._toastr.error(
                `${
                  err?.error?.detail
                    ? err?.error?.detail
                    : 'При удалении материалов возникла ошибка'
                }`,
                'Что-то пошло не так',
                { timeOut: 3500 }
              );
              return err;
            })
          );
        })
      );
    },
    { dispatch: false }
  );
}
